import { parseMarkdownTableSafe, segmentContent } from '@/lib/markdown-utils';

export type CoachingCtaKind = 'challenge' | 'academy';

export interface CoachingCtaCardData {
  kind: CoachingCtaKind;
  title: string;
  description: string;
  label: string;
  href: string;
}

export interface CoachingCtaSectionData {
  title: string;
  cards: CoachingCtaCardData[];
}

const CTA_SECTION_TITLE = /(?:\bcta\b|call\s+to\s+action|next\s+steps?|行动(?:号召|入口|建议|计划)|下一步)/i;
const AIFO_HOST = /(?:^|\.)aifo\.com$/i;
const CHALLENGE_URL_PATH = /^\/programs\/?$/i;
const ACADEMY_URL_PATH = /^\/academy\/?$/i;
const CARD_NUMBER_PREFIX = /^\s*(?:(?:\d+)(?:\uFE0F?\u20E3|[.)、．:：-])?|[①-⑳]|[一二三四五六七八九十]+[、.．])\s*/u;
const CARD_UNICODE_ICON_PREFIX = /^(?:[\p{Extended_Pictographic}\p{Emoji_Presentation}\uFE0F\u200D]\s*)+/u;
const DISPLAYED_URL_SUFFIX = /\s*(?:→|->)?\s*https?:\/\/\S+\s*$/i;
const TRAILING_ARROW = /\s*(?:→|->)\s*$/;

function stripInlineMarkdown(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[*_~`]+/g, '')
    .replace(/^\s*[-+>]\s*/gm, '')
    .replace(/\\([()])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function getSafeHref(value: string): string | null {
  const href = value.trim();

  try {
    const url = new URL(href);
    return url.protocol === 'http:' || url.protocol === 'https:' ? href : null;
  } catch {
    return null;
  }
}

function getContainingLine(block: string, matchIndex: number, matchLength: number): string {
  const lineStart = block.lastIndexOf('\n', matchIndex - 1) + 1;
  const nextLineBreak = block.indexOf('\n', matchIndex + matchLength);
  const lineEnd = nextLineBreak === -1 ? block.length : nextLineBreak + 1;
  return block.slice(lineStart, lineEnd);
}

function stripActionDecorations(value: string): string {
  return stripInlineMarkdown(value)
    .replace(/[🔗↗️]/gu, ' ')
    .replace(/(?:→|->|—|-|:)\s*$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanCardTitle(value: string): string {
  return stripInlineMarkdown(value).replace(CARD_NUMBER_PREFIX, '').replace(CARD_UNICODE_ICON_PREFIX, '').trim();
}

function cleanActionLabel(value: string): string {
  const original = stripInlineMarkdown(value).trim();
  const cleaned = original.replace(DISPLAYED_URL_SUFFIX, '').replace(TRAILING_ARROW, '').trim();
  return cleaned || original;
}

function getActionSource(block: string, matchedSource: string, matchIndex: number, forceWholeLine = false): string {
  const containingLine = getContainingLine(block, matchIndex, matchedSource.length);
  const remainder = stripActionDecorations(containingLine.replace(matchedSource, ''));
  return forceWholeLine || !remainder ? containingLine : matchedSource;
}

function extractActionLink(block: string): { label: string; href: string; source: string } | null {
  const markdownLinks = [...block.matchAll(/\[([^\]]+)\]\(([^\s)]+)(?:\s+["'][^"']*["'])?\)/g)];
  const markdownLink = markdownLinks.at(-1);

  if (markdownLink) {
    const href = getSafeHref(markdownLink[2]);
    if (href) {
      return {
        label: stripInlineMarkdown(markdownLink[1]),
        href,
        source: getActionSource(block, markdownLink[0], markdownLink.index ?? 0),
      };
    }
  }

  const htmlLinks = [...block.matchAll(/<a\s+[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi)];
  const htmlLink = htmlLinks.at(-1);

  if (htmlLink) {
    const href = getSafeHref(htmlLink[1]);
    if (href) {
      return {
        label: stripInlineMarkdown(htmlLink[2]),
        href,
        source: getActionSource(block, htmlLink[0], htmlLink.index ?? 0),
      };
    }
  }

  const bareUrls = [...block.matchAll(/https?:\/\/[^\s<>"')\]]+/g)];
  const bareUrl = bareUrls.at(-1);

  if (bareUrl) {
    const rawHref = bareUrl[0];
    const href = getSafeHref(rawHref.replace(/[.,;:!?，。；：！？]+$/u, ''));
    if (href) {
      const matchIndex = bareUrl.index ?? 0;
      const lineStart = block.lastIndexOf('\n', matchIndex - 1) + 1;
      const labelPrefix = block.slice(lineStart, matchIndex);

      return {
        label: stripActionDecorations(labelPrefix) || href,
        href,
        source: getActionSource(block, rawHref, matchIndex, true),
      };
    }
  }

  return null;
}

function getCardKindFromHref(href: string): CoachingCtaKind | null {
  try {
    const url = new URL(href);
    if (!AIFO_HOST.test(url.hostname)) return null;

    const path = url.pathname;
    if (CHALLENGE_URL_PATH.test(path)) return 'challenge';
    if (ACADEMY_URL_PATH.test(path)) return 'academy';
  } catch {
    return null;
  }

  return null;
}

function parseCard(title: string, body: string): CoachingCtaCardData | null {
  const action = extractActionLink(body);
  if (!action) return null;

  const kind = getCardKindFromHref(action.href);
  if (!kind) return null;

  const description = stripInlineMarkdown(body.replace(action.source, ''));
  if (!description) return null;

  return {
    kind,
    title: cleanCardTitle(title),
    description,
    label: cleanActionLabel(action.label),
    href: action.href,
  };
}

function parseTableCards(lines: string[]): CoachingCtaCardData[] {
  const cards: CoachingCtaCardData[] = [];
  const tableSegments = segmentContent(lines.join('\n')).filter((segment) => segment.type === 'table');

  for (const segment of tableSegments) {
    const table = parseMarkdownTableSafe(segment.content);
    if (!table) continue;

    for (const row of table.rows) {
      let actionCellIndex = -1;
      let action: ReturnType<typeof extractActionLink> = null;
      let kind: CoachingCtaKind | null = null;

      for (let index = 0; index < row.length; index += 1) {
        const parsedAction = extractActionLink(row[index]);
        if (!parsedAction) continue;
        const parsedKind = getCardKindFromHref(parsedAction.href);
        if (!parsedKind) continue;

        actionCellIndex = index;
        action = parsedAction;
        kind = parsedKind;
        break;
      }

      if (!action || !kind) continue;

      const textCells = row
        .filter((_cell, index) => index !== actionCellIndex)
        .map((cell) => stripInlineMarkdown(cell))
        .filter(Boolean);
      const title = cleanCardTitle(textCells.shift() || '');
      if (!title) continue;

      cards.push({
        kind,
        title,
        description: textCells.join(' '),
        label: cleanActionLabel(action.label),
        href: action.href,
      });
    }
  }

  return cards;
}

function getCardPair(cards: CoachingCtaCardData[]): CoachingCtaCardData[] | null {
  const pair: CoachingCtaCardData[] = [];

  for (const card of cards) {
    if (!pair.some((selectedCard) => selectedCard.kind === card.kind)) {
      pair.push(card);
    }

    if (pair.length === 2) return pair;
  }

  return null;
}

function getSectionTitle(line: string): string {
  return stripInlineMarkdown(line.replace(/^\s{0,3}#{1,6}\s+/, '').replace(/\s*#*\s*$/, ''));
}

function parseHeadingCards(lines: string[], parentLevel: number): CoachingCtaCardData[] {
  const headings = lines
    .map((line, index) => {
      const match = line.match(/^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/);
      if (!match || match[1].length <= parentLevel) return null;
      return { index, title: match[2] };
    })
    .filter((value): value is { index: number; title: string } => value !== null);

  return headings
    .map((heading, index) => {
      const end = headings[index + 1]?.index ?? lines.length;
      return parseCard(heading.title, lines.slice(heading.index + 1, end).join('\n'));
    })
    .filter((card): card is CoachingCtaCardData => card !== null);
}

function parseBoldCards(lines: string[]): CoachingCtaCardData[] {
  const headings = lines
    .map((line, index) => {
      const match = line.match(/^\s*(?:[-+]\s+)?\*\*(.+?)\*\*\s*:?[ \t]*$/);
      return match ? { index, title: match[1] } : null;
    })
    .filter((value): value is { index: number; title: string } => value !== null);

  return headings
    .map((heading, index) => {
      const end = headings[index + 1]?.index ?? lines.length;
      return parseCard(heading.title, lines.slice(heading.index + 1, end).join('\n'));
    })
    .filter((card): card is CoachingCtaCardData => card !== null);
}

function parseSection(lines: string[], headingIndex: number, endIndex: number, parentLevel: number): CoachingCtaSectionData | null {
  const title = getSectionTitle(lines[headingIndex]);
  const bodyLines = lines.slice(headingIndex + 1, endIndex);
  const headingCards = parseHeadingCards(bodyLines, parentLevel);
  const boldCards = parseBoldCards(bodyLines);
  const tableCards = parseTableCards(bodyLines);
  const cards = [headingCards, boldCards, tableCards].map(getCardPair).find((group) => group !== null);

  if (!cards) return null;

  return {
    title,
    cards,
  };
}

function parseUrlMatchedTableSection(lines: string[], headingIndex: number, endIndex: number): CoachingCtaSectionData | null {
  const cards = getCardPair(parseTableCards(lines.slice(headingIndex + 1, endIndex)));
  if (!cards) return null;

  return {
    title: getSectionTitle(lines[headingIndex]),
    cards,
  };
}

function getSectionEnd(lines: string[], headingIndex: number, parentLevel: number): number {
  for (let nextIndex = headingIndex + 1; nextIndex < lines.length; nextIndex += 1) {
    const nextHeading = lines[nextIndex].match(/^\s{0,3}(#{1,6})\s+/);
    if (nextHeading && nextHeading[1].length <= parentLevel) return nextIndex;
  }

  return lines.length;
}

function replaceSection(lines: string[], startIndex: number, endIndex: number, section: CoachingCtaSectionData): string {
  const replacement = `<div data-coaching-cta="${encodeCoachingCtaSection(section)}"></div>`;
  const before = lines.slice(0, startIndex).join('\n').trimEnd();
  const after = lines.slice(endIndex).join('\n').trimStart();
  return [before, replacement, after].filter(Boolean).join('\n\n');
}

export function encodeCoachingCtaSection(section: CoachingCtaSectionData): string {
  return encodeURIComponent(JSON.stringify(section));
}

export function decodeCoachingCtaSection(value: unknown): CoachingCtaSectionData | null {
  if (typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(decodeURIComponent(value)) as Partial<CoachingCtaSectionData>;
    if (typeof parsed.title !== 'string' || !Array.isArray(parsed.cards)) return null;

    const cards = parsed.cards.filter((card): card is CoachingCtaCardData => {
      if (!card || typeof card !== 'object') return false;
      return (
        (card.kind === 'challenge' || card.kind === 'academy') &&
        typeof card.title === 'string' &&
        typeof card.description === 'string' &&
        typeof card.label === 'string' &&
        getSafeHref(card.href) !== null
      );
    });

    if (cards.length < 2) return null;
    return { title: parsed.title, cards: cards.slice(0, 2) };
  } catch {
    return null;
  }
}

/**
 * Replaces a completed coaching-report CTA section with an inert data element.
 * ReactMarkdown + rehypeRaw then delegates that element to the card renderer.
 * Unrecognized or partial markdown is left untouched.
 */
export function injectCoachingCtaSection(markdown: string): string {
  const lines = markdown.split('\n');

  for (let index = 0; index < lines.length; index += 1) {
    const heading = lines[index].match(/^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (!heading || !CTA_SECTION_TITLE.test(stripInlineMarkdown(heading[2]))) continue;

    const parentLevel = heading[1].length;
    const endIndex = getSectionEnd(lines, index, parentLevel);
    const section = parseSection(lines, index, endIndex, parentLevel);
    if (!section) continue;

    return replaceSection(lines, index, endIndex, section);
  }

  // Table-based CTA reports can be localized to any language. Match them by the
  // two exact AIFO destination paths, not by translated headings or labels.
  // Scanning from the bottom selects the nearest enclosing heading instead of a
  // broad report heading that may contain the same table further down.
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const heading = lines[index].match(/^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (!heading) continue;

    const parentLevel = heading[1].length;
    const endIndex = getSectionEnd(lines, index, parentLevel);
    const section = parseUrlMatchedTableSection(lines, index, endIndex);
    if (!section) continue;

    return replaceSection(lines, index, endIndex, section);
  }

  return markdown;
}
